import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import DatePicker from "react-datepicker";
import Icon, { COLORS, ICONS } from './icon';

// This can be refactored more. The reason the sizes are here is because in the "create new GR modal" the "Add session" area is smaller
// width than the rest of the modal so had to make the grid smaller that contains the days and times. However, we can probably just 
// be smarter and make the size of the days+times proportional to the container that is holding it instead of having hard set values.
export const START_END_DATE_PICKER_SIZE = {
	SMALL: "small",
	NORMAL: "normal"
}
export interface OwnProps {
	startDate?: Date;	// If not provided starts off as blank
	endDate?: Date;	// If not provided starts off as blank
	defaultDurationMins?: number;	// Used to auto set times if startDate provided and/or a date is selected and the other date is auto selected
	datesUpdated: (startDate: Date, endDate: Date) => void;
	size?: string;
}

// So this component is probably over engineered but the general gist is that it's smart and anytime you pick a day or a time it auto updates the other
// side if necessary based on the defaultDurationMins so that the user can't mess up the dates (EX: start date being after the end date can't happen)

export default function StartEndDatePicker(props: OwnProps) {
	const {
		startDate,
		endDate,
		datesUpdated,
		size = START_END_DATE_PICKER_SIZE.NORMAL,
		defaultDurationMins = 60
	} = props

	// We store day+time separately to make comparisons easier
	const [startDay, setStartDay] = useState<Date | null>(null);
	const [startTime, setStartTime] = useState<Date | null>(null);
	const [endDay, setEndDay] = useState<Date | null>(null);
	const [endTime, setEndTime] = useState<Date | null>(null);

	useEffect(() => {
		// Extract day+time from start & end date props into local day+time states
		if (startDate) {
			const initialStartDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
			setStartDay(initialStartDay);
			const initialStartTime = new Date(0, 0, 0, startDate.getHours(), startDate.getMinutes(), startDate.getSeconds());
			setStartTime(initialStartTime);
		}
		if (endDate) {
			const initialEndDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
			setEndDay(initialEndDay);
			const initialEndTime = new Date(0, 0, 0, endDate.getHours(), endDate.getMinutes(), endDate.getSeconds());
			setEndTime(initialEndTime);
		}
	}, [])

	useEffect(() => {
		// Whenever days + times are selected update the dates in the parent
		if (startDay && startTime && endDay && endTime) {
			const updatedStartDate = new Date(startDay.getFullYear(), startDay.getMonth(), startDay.getDate(), startTime.getHours(), startTime.getMinutes(), startTime.getSeconds());
			const updatedEndDate = new Date(endDay.getFullYear(), endDay.getMonth(), endDay.getDate(), endTime.getHours(), endTime.getMinutes(), endTime.getSeconds());
			datesUpdated(updatedStartDate, updatedEndDate)
		}
	}, [startDay, startTime, endDay, endTime])

	function daySelected(isStartDay: boolean, selectedDay: Date) {
		if (isStartDay) {
			setStartDay(selectedDay);
			// Check if we need to modify the end day based on the start day selection
			if (!endDay || selectedDay >= endDay) {
				let dateToAssign = new Date(selectedDay);
				if (endDay && selectedDay.getDate() === endDay.getDate() && startTime && endTime && (startTime.getHours() > endTime.getHours() || (startTime.getHours() === endTime.getHours() && startTime.getMinutes() >= endTime.getMinutes()))) {
					// Selected start day is the same as the end day but the start time is later in the day than the end time. Add another
					// day to the end day. (EX: same start day selected,  start time = 11:30pm, end time = 12:30am)
					dateToAssign.setDate(dateToAssign.getDate() + 1);
					setEndDay(dateToAssign);
				} else {
					// Selected start day is after the current end day
					if (startTime && endTime && (startTime.getHours() > endTime.getHours() || (startTime.getHours() === endTime.getHours() && startTime.getMinutes() >= endTime.getMinutes()))) {
						// Need to auto assign the end day to be one later than the start day because start time is later in the day than the end time
						// (EX: start time = 11:30pm, end time = 12:30am)
						dateToAssign.setDate(dateToAssign.getDate() + 1);
						setEndDay(dateToAssign);
					} else {
						// Auto assign end day same as selected start day
						setEndDay(dateToAssign);
					}
				}
			}
		} else {
			setEndDay(selectedDay)
			if (!startDay || selectedDay <= startDay) {
				let dateToAssign = new Date(selectedDay);
				if (startDay && selectedDay.getDate() === startDay.getDate() && endTime && startTime && (endTime.getHours() < startTime.getHours() || (startTime.getHours() === endTime.getHours() && endTime.getMinutes() <= startTime.getMinutes()))) {
					// Selected end day is the same as the start day but the start time is later in the day than the end time. Decrement
					// a day for the start day. (EX: same end day selected, start time = 11:30pm, end time = 12:30am)
					dateToAssign.setDate(dateToAssign.getDate() - 1);
					setStartDay(dateToAssign);
				} else {
					// Selected end day is before the current start day
					if (endTime && startTime && (endTime.getHours() < startTime.getHours() || (startTime.getHours() === endTime.getHours() && endTime.getMinutes() <= startTime.getMinutes()))) {
						dateToAssign.setDate(dateToAssign.getDate() - 1);
						setStartDay(dateToAssign);
					} else {
						// Auto assign start day same as selected end day
						setStartDay(dateToAssign);
					}
				}
			}
		}
	}

	function timeSelected(isStartTime: boolean, selectedTime: Date) {
		if (isStartTime) {
			setStartTime(selectedTime);
			// Also auto assign the end time if:
			// a) End time isn't selected yet
			// b) The days are the same and the selected start time is after end time. Compare hours and minutes instead of getTime() (because for the time variables all we care about
			// is hours and minutes. The day could be different on the time variables after auto assigning dates)
			// 	  	(i.e. Start time = 4:00pm & End Time = 2:00pm or Start time = 4:45pm & End Time = 4:15pm)
			if (!endTime || (startDay && endDay && startDay.getDate() === endDay.getDate() &&
				(selectedTime.getHours() > endTime.getHours() || (selectedTime.getHours() === endTime.getHours() && selectedTime.getMinutes() >= endTime.getMinutes())))) {
				const updatedEndTime = new Date(selectedTime);
				updatedEndTime.setMinutes(selectedTime.getMinutes() + defaultDurationMins);
				setEndTime(updatedEndTime);

				// Increase end day by 1 if automatic end time assignment leaks into next day (ie 11:50pm -> 12:50am)
				if (endDay && updatedEndTime.getHours() < selectedTime.getHours()) {
					const updatedEndDay = new Date(endDay);
					updatedEndDay.setDate(updatedEndDay.getDate() + 1);
					setEndDay(updatedEndDay);
				}
			}
		} else {
			setEndTime(selectedTime);
			// Auto assign the start time when the end time is selected if:
			// a) Start time doesnt exist yet
			// b) The days are the same and the selected end time is equal to or before the selected start time. Compare hours and minutes instead of getTime() (because for the time variables all we care about
			// is hours and minutes. The day could be different on the time variables after auto assigning dates)
			// 		(i.e.Start time = 4:00pm & End Time = 2:00pm or Start time = 4:45pm & End Time = 4:15pm)
			if (!startTime || (startDay && endDay && startDay.getDate() === endDay.getDate() &&
				(selectedTime.getHours() < startTime.getHours() || (selectedTime.getHours() === startTime.getHours() && selectedTime.getMinutes() <= startTime.getMinutes())))) {
				const updatedStartTime = new Date(selectedTime);
				updatedStartTime.setMinutes(selectedTime.getMinutes() - defaultDurationMins);
				setStartTime(updatedStartTime);

				// Decrease start day by 1 if automatic start time assignment is the previous day (ie 12:30am endtime selected, auto assign start to 11:30pm of previous day)
				if (startDay && endDay && startDay.getDate() === endDay.getDate() && updatedStartTime.getHours() > selectedTime.getHours()) {
					const updatedStartDay = new Date(startDay);
					updatedStartDay.setDate(updatedStartDay.getDate() - 1);
					setStartDay(updatedStartDay);
				}
			}
		}
	}

	return (
		<div className="start-end-container">
			<div className={classNames("input-header", size === START_END_DATE_PICKER_SIZE.SMALL && "small")}>
				<span data-end="*">Starts</span>
				<span data-end="*">Ends</span>
			</div>
			<div className={classNames("start-end", size === START_END_DATE_PICKER_SIZE.SMALL && "small")}>
				<div
					className={classNames("date", size === START_END_DATE_PICKER_SIZE.SMALL && "small")}
					data-testid="start-end-date-picker-start-date"
				>
					<DatePicker
						selected={startDay}
						onChange={(date) => {
							if (date) {
								daySelected(true, date);
							}
						}}
						dateFormat="MMM d yyyy"
					/>
					<Icon name={ICONS.CALENDAR} color={startDay ? COLORS.WHITE : COLORS.DARK_GRAY_2} size={13.33} />
				</div>
				<div
					className={classNames("time", size === START_END_DATE_PICKER_SIZE.SMALL && "small")}
					data-testid="start-end-date-picker-start-time"
				>
					<DatePicker
						selected={startTime}
						onChange={(date) => {
							if (date) {
								timeSelected(true, date);
							}
						}}
						dateFormat="h:mm aa"
						showTimeSelect
						showTimeSelectOnly
						timeIntervals={5}
					/>
					<Icon name={ICONS.CLOCK_ALT} color={startTime ? COLORS.WHITE : COLORS.DARK_GRAY_2} size={13.33} />
				</div>
				<span className={classNames("start-end-divider", size === START_END_DATE_PICKER_SIZE.SMALL && "small")}>–</span>
				<div
					className={classNames("date", size === START_END_DATE_PICKER_SIZE.SMALL && "small")}
					data-testid="start-end-date-picker-end-date"
				>
					<DatePicker
						selected={endDay}
						onChange={(date) => {
							if (date) {
								daySelected(false, date);
							}
						}}
						dateFormat="MMM d yyyy"
					/>
					<Icon name={ICONS.CALENDAR} color={endDay ? COLORS.WHITE : COLORS.DARK_GRAY_2} size={13.33} />
				</div>
				<div
					className={classNames("time", size === START_END_DATE_PICKER_SIZE.SMALL && "small")}
					data-testid="start-end-date-picker-end-time"
				>
					<DatePicker
						selected={endTime}
						onChange={(date) => {
							if (date) {
								timeSelected(false, date);
							}
						}}
						dateFormat="h:mm aa"
						showTimeSelect
						showTimeSelectOnly
						timeIntervals={5}
					/>
					<Icon name={ICONS.CLOCK_ALT} color={endTime ? COLORS.WHITE : COLORS.DARK_GRAY_2} size={13.33} />
				</div>
			</div>
		</div>
	)
}
